import React, { useState, useEffect } from 'react'
import ContainerDashboard from '../../components/layouts/ContainerDashboard'
import { useNavigate, useParams } from 'react-router-dom'
import { axiosInstance } from '../Config'

const avatarColors = ['#A44CD7', '#0CACC2', '#F55145', '#F9BE22', '#8A3BBD', '#19C37D', '#5A79DF', '#EC2434']
const getInitials = (name = '') => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)

const formatDate = (dateStr) => {
    if (!dateStr) return '—'
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })
}

const STATUS_BADGE = {
    scheduled:  { bg: 'rgba(255,255,255,0.25)', color: '#fff' },
    completed:  { bg: '#19C37D',                color: '#fff' },
    cancelled:  { bg: '#EC2434',                color: '#fff' },
}

const TABS = [
    { key: 'today',    icon: 'bi-calendar-day',   label: 'Today' },
    { key: 'upcoming', icon: 'bi-calendar-check',  label: 'Upcoming' },
    { key: 'past',     icon: 'bi-clock-history',   label: 'Past' },
]

const TutorSlots = () => {
    const { tutorId } = useParams()
    const navigate = useNavigate()

    const [activeTab, setActiveTab] = useState('today')
    const [sessions, setSessions] = useState([])
    const [counts, setCounts] = useState({ today: 0, upcoming: 0, past: 0 })
    const [loading, setLoading] = useState(false)
    const [tutor, setTutor] = useState(null)

    useEffect(() => {
        document.title = 'Tutor Sessions | Mindora'
        window.scrollTo(0, 0)
        axiosInstance.get(`tutors/${tutorId}`)
            .then(res => setTutor(res.data?.data ?? res.data))
            .catch(() => {})
    }, [tutorId])

    useEffect(() => {
        fetchSessions(activeTab)
    }, [activeTab, tutorId])

    useEffect(() => {
        const loadAllCounts = async () => {
            const results = await Promise.all(
                TABS
                    .filter(tab => tab.key !== activeTab)
                    .map(tab => axiosInstance.get(`tutors/${tutorId}/sessions?tab=${tab.key}`)
                        .then(res => {
                            const data = res.data?.data ?? res.data
                            return { tab: tab.key, count: (data.sessions ?? []).length }
                        })
                        .catch(() => ({ tab: tab.key, count: 0 }))
                    )
            )
            setCounts(prev => {
                const next = { ...prev }
                results.forEach(r => { next[r.tab] = r.count })
                return next
            })
        }
        loadAllCounts()
    }, [tutorId])

    const fetchSessions = async (tab) => {
        setLoading(true)
        try {
            const res = await axiosInstance.get(`tutors/${tutorId}/sessions?tab=${tab}`)
            const data = res.data?.data ?? res.data
            setSessions(data.sessions ?? [])
            setCounts(prev => ({ ...prev, [tab]: (data.sessions ?? []).length }))
        } catch {
            setSessions([])
        } finally {
            setLoading(false)
        }
    }

    return (
        <ContainerDashboard title="Tutor Sessions" description="View tutor sessions">
            <section className="ps-dashboards">
                <div className="p-4 py-5 bg-gradientt rounded-4" style={{ minHeight: '75vh' }}>

                    {/* Header */}
                    <div className='d-md-flex justify-content-between align-items-center mb-4'>
                        <div className='d-flex align-items-center gap-3'>
                            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: 52, height: 52, background: avatarColors[Number(tutorId) % avatarColors.length] || '#A44CD7', color: '#fff', fontWeight: 700, fontSize: 18 }}>
                                {getInitials(tutor?.name || '')}
                            </div>
                            <div>
                                <h5 className='mb-0 fw-bold'>{tutor?.name || 'Tutor'}</h5>
                                <p className='mb-0 text-muted' style={{ fontSize: 13 }}>{tutor?.qualification || ''}</p>
                            </div>
                        </div>
                        <div className='d-flex gap-2 mt-3 mt-md-0'>
                            <button className='button'
                                style={{ background: '#1a1a2e', color: '#fff', borderRadius: '12px' }}
                                onClick={() => navigate(-1)}>
                                <i className='icon-arrow-left me-2'></i>Back
                            </button>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className='tutor-slot-tabs mb-4'>
                        {TABS.map(tab => (
                            <button key={tab.key}
                                className={`tutor-slot-tab ${activeTab === tab.key ? 'active' : ''}`}
                                onClick={() => setActiveTab(tab.key)}>
                                <i className={`bi ${tab.icon} me-2`}></i>
                                {tab.label}
                                <span className='ms-2 tutor-tab-badge' style={{
                                    background: activeTab === tab.key ? '#fff' : '#e9ecef',
                                    color: activeTab === tab.key ? 'var(--primary)' : '#666',
                                }}>
                                    {counts[tab.key] ?? 0}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Session Cards */}
                    {loading ? (
                        <div className='text-center py-5'>
                            <div className="spinner-border text-primary" role="status"></div>
                        </div>
                    ) : sessions.length === 0 ? (
                        <div className='text-center py-5'>
                            <i className='bi bi-calendar-x' style={{ fontSize: 52, color: '#ccc' }}></i>
                            <p className='mt-3 text-muted fs-5'>No {activeTab} sessions found.</p>
                        </div>
                    ) : (
                        <div className='row g-3'>
                            {sessions.map(session => {
                                const badgeStyle = STATUS_BADGE[session.status] ?? STATUS_BADGE.scheduled
                                let bookedUser = session.bookedBy
                                if (typeof bookedUser === 'string') {
                                    try { bookedUser = JSON.parse(bookedUser)[0] } catch { bookedUser = null }
                                }
                                bookedUser = bookedUser?.[0] ?? bookedUser

                                return (
                                    <div key={session.id} className='col-md-6'>
                                        <div className='tutor-slot-card'>
                                            <div className='tutor-slot-card-header'>
                                                <div>
                                                    <p className='mb-0 fw-bold' style={{ fontSize: 15, color: '#fff' }}>
                                                        {session.topic || 'Session'}
                                                    </p>
                                                    <p className='mb-0' style={{ fontSize: 12, color: 'rgba(255,255,255,0.82)' }}>
                                                        {formatDate(session.date)}
                                                    </p>
                                                </div>
                                                <span className='badge' style={{ background: badgeStyle.bg, color: badgeStyle.color, fontSize: '11px', flexShrink: 0, textTransform: 'capitalize' }}>
                                                    {session.status}
                                                </span>
                                            </div>

                                            <div className='tutor-slot-card-body'>
                                                <div className='d-flex align-items-center gap-2 mb-2'>
                                                    <i className='bi bi-clock' style={{ color: 'var(--primary)', fontSize: 14 }}></i>
                                                    <span style={{ fontSize: 13.5 }}>{session.time}</span>
                                                    {session.duration && (
                                                        <span style={{ fontSize: 12, color: '#999', marginLeft: 4 }}>({session.duration})</span>
                                                    )}
                                                </div>

                                                {session.notes && (
                                                    <div className='d-flex align-items-start gap-2 mb-2'>
                                                        <i className='bi bi-chat-left-text' style={{ color: 'var(--primary)', fontSize: 14, marginTop: 2 }}></i>
                                                        <span style={{ fontSize: 13, color: '#555' }}>{session.notes}</span>
                                                    </div>
                                                )}

                                                {bookedUser && (
                                                    <div className='tutor-booked-section'>
                                                        <p className='tutor-booked-label'>
                                                            <i className='bi bi-person me-1'></i>Student
                                                        </p>
                                                        <span className='tutor-booked-badge'>{bookedUser.username}</span>
                                                    </div>
                                                )}

                                                {session.sessionLink && (
                                                    <a href={session.sessionLink} target='_blank' rel='noreferrer'
                                                        className='tutor-slot-link mt-3 d-inline-flex'>
                                                        <i className='bi bi-camera-video-fill me-2'></i>Join Session
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            </section>
        </ContainerDashboard>
    )
}

export default TutorSlots
